<template>
  <div class="content">
    <div class="md-layout">
      <div
        class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100"
      >
        <div class="back-link" v-if="canGoBack">
          <a href="#" @click.prevent="goBack">&larr; Back</a>
        </div>

        <div v-if="loading" class="text-center loading-block">
          <md-progress-spinner
            md-mode="indeterminate"
            :md-diameter="28"
            :md-stroke="3"
          ></md-progress-spinner>
        </div>

        <div v-else-if="loadError" class="text-center loading-block">
          <p>{{ loadError }}</p>
        </div>

        <template v-else-if="report">
          <page-header :title="reportTypeLabel" :subtitle="periodLabel">
            <template #actions>
              <status-pill
                :variant="reportStatusVariant(report.status)"
                :label="reportStatusLabel(report.status)"
              />
            </template>
          </page-header>

          <div class="report-card">
            <div class="meta-grid">
              <div>
                <label>Requested</label>
                <span>{{
                  format(report.created_at, 'DD/MM/YYYY hh:mm a')
                }}</span>
              </div>
              <div>
                <label>Rows</label>
                <span>{{
                  report.row_count != null ? commaNumber(report.row_count) : '—'
                }}</span>
              </div>
              <div>
                <label>Size</label>
                <span>{{
                  report.file_size != null ? formatBytes(report.file_size) : '—'
                }}</span>
              </div>
            </div>

            <!-- Still generating -->
            <div v-if="isInProgress" class="state-note">
              <md-progress-bar md-mode="indeterminate"></md-progress-bar>
              <p>
                We're generating this report. You'll get an email with a
                download link as soon as it's ready.
              </p>
            </div>

            <!-- Ready -->
            <div v-else-if="isGenerated" class="state-note">
              <notice-banner title="We emailed this report to you.">
                If it hasn't arrived [or the link is no longer active], download
                it here instead — the link is good for one minute and only works
                while you're signed in.
              </notice-banner>
              <div class="actions">
                <app-button :disabled="downloading" @click="downloadReport()">
                  {{ downloading ? 'Preparing…' : 'Download' }}
                </app-button>
                <app-button
                  variant="ghost"
                  :disabled="sending"
                  @click="emailReport()"
                >
                  {{ sending ? 'Sending…' : 'Email it again' }}
                </app-button>
              </div>
            </div>

            <!-- Failed or expired -->
            <div v-else-if="isFailed || isExpired" class="state-note">
              <p>
                {{
                  isExpired
                    ? 'This report has expired and can no longer be sent.'
                    : 'This report failed to generate.'
                }}
                Request a new one to get a fresh copy.
              </p>
              <app-button
                variant="ghost"
                :disabled="requestingAgain"
                @click="requestAgain()"
              >
                {{ requestingAgain ? 'Requesting…' : 'Request again' }}
              </app-button>
            </div>
          </div>

          <template v-if="report.deliveries && report.deliveries.length">
            <h4 class="deliveries-title">Delivery history</h4>
            <div class="report-card">
              <md-table class="deliveries-table">
                <md-table-row>
                  <md-table-head>Attempt</md-table-head>
                  <md-table-head>Sent to</md-table-head>
                  <md-table-head>Status</md-table-head>
                  <md-table-head>When</md-table-head>
                </md-table-row>
                <md-table-row
                  v-for="delivery in report.deliveries"
                  :key="delivery.id"
                >
                  <md-table-cell>#{{ delivery.attempt_number }}</md-table-cell>
                  <md-table-cell>{{ delivery.recipient_email }}</md-table-cell>
                  <md-table-cell>
                    <status-pill
                      :variant="reportStatusVariant(delivery.status)"
                      :label="reportStatusLabel(delivery.status)"
                    />
                  </md-table-cell>
                  <md-table-cell>{{ deliveryWhen(delivery) }}</md-table-cell>
                </md-table-row>
              </md-table>
            </div>
          </template>
        </template>
      </div>
    </div>

    <md-snackbar
      md-position="center"
      :md-duration="6000"
      :md-active.sync="snackBar.show"
      md-persistent
    >
      <span>{{ snackBar.message }}</span>
    </md-snackbar>
  </div>
</template>

<script>
import { format } from 'date-fns';
import http from '../../utils/Http.util';
import { commaNumber } from '../../utils/Formatter.util';
import {
  reportStatusLabel,
  reportStatusVariant,
} from '../../utils/reportStatus.util';
import PageHeader from '../../components/ui/PageHeader.vue';
import StatusPill from '../../components/ui/StatusPill.vue';
import AppButton from '../../components/ui/AppButton.vue';
import NoticeBanner from '../../components/ui/NoticeBanner.vue';

const REPORT_CHANNEL = 'apix';

const IN_PROGRESS_STATUSES = ['pending', 'queued', 'generating'];

export default {
  name: 'report-detail',
  components: {
    PageHeader,
    StatusPill,
    AppButton,
    NoticeBanner,
  },
  data() {
    return {
      report: null,
      loading: true,
      loadError: null,
      sending: false,
      downloading: false,
      requestingAgain: false,
      snackBar: {
        show: false,
        message: null,
      },
    };
  },
  computed: {
    canGoBack() {
      return window.history.length > 1;
    },
    reportTypeLabel() {
      return this.report.report_type === 'wallet'
        ? 'Wallet report'
        : 'Transaction report';
    },
    periodLabel() {
      const { filters } = this.report;
      if (!filters || !filters.startDate) return '';
      const start = format(filters.startDate, 'D MMM YYYY');
      const end = format(filters.endDate, 'D MMM YYYY');
      return `${start} – ${end}`;
    },
    isInProgress() {
      return IN_PROGRESS_STATUSES.includes(this.report.status);
    },
    isGenerated() {
      return this.report.status === 'generated';
    },
    isFailed() {
      return this.report.status === 'failed';
    },
    isExpired() {
      return this.report.status === 'expired';
    },
  },
  async mounted() {
    await this.fetchReport();
  },
  methods: {
    format,
    commaNumber,
    reportStatusLabel,
    reportStatusVariant,
    formatBytes(bytes) {
      const kb = bytes / 1024;
      if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
      return `${(kb / 1024).toFixed(1)} MB`;
    },
    goBack() {
      this.$router.back();
    },
    deliveryWhen(delivery) {
      const at = delivery.sent_at || delivery.failed_at || delivery.created_at;
      return at ? format(at, 'DD/MM/YYYY hh:mm a') : '—';
    },
    async fetchReport() {
      this.loading = true;
      this.loadError = null;
      try {
        const { data } = await http.authReq(
          `/dashboard/reports/${this.$route.params.id}`,
          { channel: REPORT_CHANNEL, include: 'deliveries' },
        );
        this.report = data;
      } catch (err) {
        this.loadError = err.message || 'We could not load this report.';
      } finally {
        this.loading = false;
      }
    },
    // The signed URL is deliberately short-lived, so it's minted on the click
    // rather than carried on the report. Navigating rather than fetching a blob
    // keeps the transfer between the browser and S3 — it streams to disk,
    // survives a deploy mid-download, and never buffers the whole file in
    // memory on a low-end device.
    async downloadReport() {
      this.downloading = true;
      try {
        const { meta } = await http.authReq(
          `/dashboard/reports/${this.$route.params.id}/downloads`,
          {},
          'post',
        );

        // No `download` attribute: it is ignored cross-origin anyway, and the
        // Content-Disposition signed into the URL is what actually names the
        // file and keeps the page put.
        const link = document.createElement('a');
        link.href = meta.url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        this.snackBar = {
          show: true,
          message: err.message || 'We could not prepare your download.',
        };
      } finally {
        this.downloading = false;
      }
    },

    async emailReport() {
      this.sending = true;
      try {
        await http.authReq(
          `/dashboard/reports/${this.$route.params.id}/deliveries`,
          {},
          'post',
        );
        this.snackBar = {
          show: true,
          message: 'Sent! Check your inbox for the download link.',
        };
        await this.fetchReport();
      } catch (err) {
        this.snackBar = {
          show: true,
          message: err.message || 'Something went wrong. Please try again.',
        };
      } finally {
        this.sending = false;
      }
    },
    async requestAgain() {
      this.requestingAgain = true;
      try {
        const { filters } = this.report;
        const body = {
          channel: REPORT_CHANNEL,
          report_type: this.report.report_type,
          start_date: filters.startDate,
          end_date: filters.endDate,
        };
        const { data } = await http.authReq('/dashboard/reports', body, 'post');
        this.$router.push(`/reports/${data.id}`);
      } catch (err) {
        this.snackBar = {
          show: true,
          message: err.message || 'Something went wrong. Please try again.',
        };
        this.requestingAgain = false;
      }
    },
  },
};
</script>

<style scoped>
.back-link {
  margin-bottom: 16px;
}
.back-link a {
  color: var(--muted);
  font-size: 13px;
  text-decoration: none;
}
.back-link a:hover {
  color: var(--brand-dark);
}

.loading-block {
  padding: 60px 0;
}

.report-card {
  background: var(--surface);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  padding: 20px;
  margin-bottom: 20px;
}

.meta-grid {
  display: flex;
  gap: 40px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border);
}
.meta-grid label {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--faint);
  margin-bottom: 4px;
}
.meta-grid span {
  font-size: 14px;
}

.state-note p {
  color: var(--muted);
  margin: 8px 0 14px;
}
.state-note {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}
.state-note .actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.deliveries-title {
  margin: 24px 0 8px;
}

.deliveries-table >>> .md-table-row:first-child {
  background: var(--brand);
}
.deliveries-table >>> .md-table-row:first-child .md-table-head-label {
  color: var(--brand-ink);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
}
/* The app's own global _tables.scss forces every row's hover background
   to #f5f5f5 with !important — without this, the branded header row
   flashes gray on hover since it isn't actually clickable. */
.deliveries-table >>> .md-table-row:first-child:hover {
  background: var(--brand) !important;
}
</style>
